import type * as React from 'react'
import type {
  OutputColumn,
  OutputColumnType,
  OutputTable
} from '@axilon-apps/domains-ontology-ui-state'
import { TABULAR_DUMP_COLUMN_TYPES } from '@atbash-labs/ontology-core'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import { Input } from '../../../components/ui/input'
import { NativeSelect, NativeSelectOption } from '../../../components/ui/native-select'
import {
  EDITOR_BODY_CLASS,
  EDITOR_CARD_CLASS,
  EDITOR_INPUT_CLASS,
  EDITOR_SELECT_CLASS
} from './editorChrome'

/** The dump plane's column types, the same list the service validates against. */
export const OUTPUT_COLUMN_TYPES: readonly OutputColumnType[] = TABULAR_DUMP_COLUMN_TYPES

const WRITE_MODES: OutputTable['mode'][] = ['replace', 'append', 'upsert']

const newColumn = (): OutputColumn => ({ name: '', type: 'VARCHAR' })
const newTable = (): OutputTable => ({
  table: '',
  mode: 'replace',
  key: [],
  columns: [newColumn()]
})

/**
 * Tables, write mode and key, columns, types, required flags and hints — the
 * editable body of a contract, shared by *Edit contract* and *Send back with
 * changes*. Validation and submit stay with the dialog that hosts it.
 */
export function ContractTablesEditor({
  tables,
  onChange
}: {
  tables: OutputTable[]
  onChange: React.Dispatch<React.SetStateAction<OutputTable[]>>
}) {
  const updateTable = (ti: number, patch: Partial<OutputTable>) =>
    onChange((current) => current.map((table, i) => (i === ti ? { ...table, ...patch } : table)))
  const updateColumn = (ti: number, ci: number, patch: Partial<OutputColumn>) =>
    onChange((current) =>
      current.map((table, i) =>
        i === ti
          ? {
              ...table,
              columns: table.columns.map((column, j) =>
                j === ci ? { ...column, ...patch } : column
              )
            }
          : table
      )
    )
  const toggleKey = (ti: number, column: string, on: boolean) =>
    onChange((current) =>
      current.map((table, i) =>
        i === ti
          ? { ...table, key: on ? [...table.key, column] : table.key.filter((k) => k !== column) }
          : table
      )
    )

  return (
    <div className={EDITOR_BODY_CLASS}>
      {tables.map((table, ti) => (
        <div key={ti} className={`${EDITOR_CARD_CLASS} flex flex-col gap-2`}>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label={`Table ${ti + 1} name`}
              className={`${EDITOR_INPUT_CLASS} max-w-[16rem] font-mono`}
              placeholder="table_name"
              value={table.table}
              onChange={(event) => updateTable(ti, { table: event.target.value })}
            />
            <NativeSelect
              size="sm"
              className={EDITOR_SELECT_CLASS}
              aria-label={`Table ${ti + 1} write mode`}
              value={table.mode}
              onChange={(event) =>
                updateTable(ti, { mode: event.target.value as OutputTable['mode'] })
              }
            >
              {WRITE_MODES.map((mode) => (
                <NativeSelectOption key={mode} value={mode}>
                  {mode}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <label className="flex items-center gap-1.5 text-11 text-ink-2">
              <Checkbox
                className="size-3.5"
                aria-label={`Table ${ti + 1} once per as-of date`}
                checked={table.ingestOncePerAsOfDate === true}
                onCheckedChange={(checked) =>
                  updateTable(ti, {
                    ingestOncePerAsOfDate: checked === true ? true : undefined
                  })
                }
              />
              once per as-of date
            </label>
            <span className="flex-1" />
            <span className="text-11 text-ink-3 tabular-nums">
              {table.columns.length} column{table.columns.length === 1 ? '' : 's'}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="text-ink-3 hover:text-st-down"
              aria-label={`Remove table ${table.table || ti + 1}`}
              onClick={() => onChange((current) => current.filter((_, i) => i !== ti))}
            >
              <Trash2 />
            </Button>
          </div>
          <table className="w-full table-fixed border-separate border-spacing-0 text-xs">
            <colgroup>
              <col className="w-[28%]" />
              <col className="w-[8.5rem]" />
              <col className="w-14" />
              <col className="w-10" />
              <col />
              <col className="w-7" />
            </colgroup>
            <thead className="text-left text-10 font-medium tracking-wide text-ink-3 uppercase">
              <tr>
                <th className="pb-1 pr-2 font-medium">Column</th>
                <th className="pb-1 pr-2 font-medium">Type</th>
                <th className="pb-1 pr-2 text-center font-medium">Req.</th>
                <th className="pb-1 pr-2 text-center font-medium">Key</th>
                <th className="pb-1 pr-2 font-medium">Hint</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {table.columns.map((column, ci) => (
                <tr key={ci}>
                  <td className="py-0.5 pr-2">
                    <Input
                      aria-label={`${table.table} column ${ci + 1} name`}
                      className={`${EDITOR_INPUT_CLASS} font-mono`}
                      placeholder="column_name"
                      value={column.name}
                      onChange={(event) => {
                        const previous = column.name
                        updateColumn(ti, ci, { name: event.target.value })
                        if (table.key.includes(previous)) {
                          updateTable(ti, {
                            key: table.key.map((k) => (k === previous ? event.target.value : k))
                          })
                        }
                      }}
                    />
                  </td>
                  <td className="py-0.5 pr-2">
                    <NativeSelect
                      size="sm"
                      className={`${EDITOR_SELECT_CLASS} w-full`}
                      aria-label={`${table.table} column ${ci + 1} type`}
                      value={column.type}
                      onChange={(event) =>
                        updateColumn(ti, ci, { type: event.target.value as OutputColumnType })
                      }
                    >
                      {OUTPUT_COLUMN_TYPES.map((type) => (
                        <NativeSelectOption key={type} value={type}>
                          {type}
                        </NativeSelectOption>
                      ))}
                    </NativeSelect>
                  </td>
                  <td className="py-0.5 pr-2 text-center">
                    <Checkbox
                      className="size-3.5"
                      aria-label={`${table.table} column ${ci + 1} required`}
                      checked={column.required === true}
                      onCheckedChange={(checked) =>
                        updateColumn(ti, ci, { required: checked === true ? true : undefined })
                      }
                    />
                  </td>
                  <td className="py-0.5 pr-2 text-center">
                    <Checkbox
                      className="size-3.5"
                      aria-label={`${table.table} column ${ci + 1} in key`}
                      checked={table.key.includes(column.name)}
                      onCheckedChange={(checked) => toggleKey(ti, column.name, checked === true)}
                    />
                  </td>
                  <td className="py-0.5 pr-2">
                    <Input
                      aria-label={`${table.table} column ${ci + 1} hint`}
                      className={EDITOR_INPUT_CLASS}
                      placeholder="What this column holds"
                      value={column.description ?? ''}
                      onChange={(event) =>
                        updateColumn(ti, ci, { description: event.target.value || undefined })
                      }
                    />
                  </td>
                  <td className="py-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      className="text-ink-3 hover:text-st-down"
                      aria-label={`Remove ${table.table} column ${ci + 1}`}
                      onClick={() =>
                        updateTable(ti, {
                          columns: table.columns.filter((_, j) => j !== ci),
                          key: table.key.filter((k) => k !== column.name)
                        })
                      }
                    >
                      <Trash2 />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="w-fit"
            onClick={() => updateTable(ti, { columns: [...table.columns, newColumn()] })}
          >
            <Plus /> Add column
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="xs"
        className="w-fit"
        onClick={() => onChange((current) => [...current, newTable()])}
      >
        <Plus /> Add table
      </Button>
    </div>
  )
}
